import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Trash2, AlertTriangle, ClipboardList, Search, Pencil, CheckSquare, Square, Users, X, CalendarClock, Library, FastForward, Zap } from "lucide-react";

const SUBJECT_COLOR = {
  English: "#E07A5F", Math: "#81B29A", Science: "#A8DADC", Bible: "#3D405B",
  "Social Studies": "#F4A261", Acting: "#81B29A", Vocabulary: "#E07A5F",
};

export default function ManageAssignments() {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const [students, setStudents] = useState([]);
  const [family, setFamily] = useState([]);
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [filter, setFilter] = useState(query.get("student") || "");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyTargets, setCopyTargets] = useState(new Set());
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedMode, setReschedMode] = useState("set");
  const [reschedDate, setReschedDate] = useState("");
  const [reschedShift, setReschedShift] = useState(7);
  const [working, setWorking] = useState(false);
  const [savingLibId, setSavingLibId] = useState(null);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushDays, setPushDays] = useState(7);
  const [pushScope, setPushScope] = useState("all");
  const [pushFrom, setPushFrom] = useState("");
  const [catchOpen, setCatchOpen] = useState(false);

  const openPush = () => { setPushScope(selected.size > 0 ? "selected" : "all"); setPushOpen(true); };

  const doCatchUp = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/assignments/catch-up", { student_id: filter || null });
      toast.success(`Pulled ${data.updated} overdue lesson${data.updated === 1 ? "" : "s"} forward to start ${data.starts}.`);
      setCatchOpen(false); clearSel(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const doPushForward = async () => {
    const days = parseInt(pushDays, 10);
    if (!days) return toast.error("Enter how many days to push forward.");
    if (pushScope === "selected" && selected.size === 0) return toast.error("Select some lessons first, or choose Push all.");
    setWorking(true);
    try {
      const payload = pushScope === "selected"
        ? { days, scope: "selected", assignment_ids: [...selected] }
        : { days, scope: "all", student_id: filter || null, from_date: pushFrom || null };
      const { data } = await api.post("/assignments/push-forward", payload);
      toast.success(`Pushed ${data.updated} lesson${data.updated === 1 ? "" : "s"} forward ${days} day${days === 1 ? "" : "s"}.`);
      setPushOpen(false); clearSel(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const saveToLibrary = async (a) => {
    setSavingLibId(a.id);
    try {
      await api.post(`/assignments/${a.id}/save-to-library`);
      toast.success(`Saved "${a.title}" to your Lesson Library.`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSavingLibId(null); }
  };

  const doBulkSaveLibrary = async () => {
    setWorking(true);
    try {
      const ids = [...selected];
      await Promise.all(ids.map((id) => api.post(`/assignments/${id}/save-to-library`)));
      toast.success(`Saved ${ids.length} lesson${ids.length === 1 ? "" : "s"} to your Library.`);
      clearSel();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const load = () => {
    setLoading(true);
    api.get("/assignments").then((r) => setAssignments(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/family/members").then((r) => setFamily(r.data)).catch(() => {});
    load();
  }, []);

  const nameOf = (id) => {
    const s = students.find((x) => x.id === id);
    if (s) return s.first_name;
    const f = family.find((x) => x.id === id);
    if (f) return `${f.first_name} (self-study)`;
    if (user && id === user.id) return `${user.first_name || "Me"} (self-study)`;
    return "Other";
  };

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/assignments/${toDelete.id}`);
      toast.success(`Deleted "${toDelete.title}".`);
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const doBulkDelete = async () => {
    setWorking(true);
    try {
      const { data } = await api.post("/assignments/bulk-delete", { assignment_ids: [...selected] });
      toast.success(`Deleted ${data.deleted} assignment${data.deleted === 1 ? "" : "s"}.`);
      setBulkDelete(false); clearSel(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const doCopy = async () => {
    if (copyTargets.size === 0) return toast.error("Pick at least one student to copy to.");
    setWorking(true);
    try {
      const { data } = await api.post("/assignments/copy", { assignment_ids: [...selected], student_ids: [...copyTargets] });
      if (data.created === 0) {
        toast.info(`Those ${data.students === 1 ? "student" : "students"} already have the selected assignment${selected.size === 1 ? "" : "s"} — nothing new to assign.`);
      } else {
        toast.success(`Assigned ${data.created} assignment${data.created === 1 ? "" : "s"} to ${data.students} student${data.students === 1 ? "" : "s"}.`);
      }
      setCopyOpen(false); setCopyTargets(new Set()); clearSel(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const doReschedule = async () => {
    if (reschedMode === "set" && !reschedDate) return toast.error("Pick a date to set.");
    if (reschedMode === "shift" && !Number.isInteger(reschedShift)) return toast.error("Enter a whole number of days.");
    setWorking(true);
    try {
      const payload = reschedMode === "set"
        ? { assignment_ids: [...selected], mode: "set", due_date: reschedDate }
        : { assignment_ids: [...selected], mode: "shift", shift_days: reschedShift };
      const { data } = await api.post("/assignments/reschedule", payload);
      let msg = `Rescheduled ${data.updated} assignment${data.updated === 1 ? "" : "s"}.`;
      if (data.skipped) msg += ` Skipped ${data.skipped} with no due date.`;
      toast.success(msg);
      setReschedOpen(false); clearSel(); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setWorking(false); }
  };

  const visible = assignments
    .filter((a) => !filter || a.student_id === filter)
    .filter((a) => !search || `${a.title} ${a.subject} ${a.chapter || ""}`.toLowerCase().includes(search.toLowerCase()));

  // group by student
  const groups = {};
  visible.forEach((a) => { (groups[a.student_id] ||= []).push(a); });

  return (
    <div>
      <TopBar subtitle="Manage Assignments" />
      <main className="max-w-5xl mx-auto px-6 md:px-8 py-10">
        <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B] mb-6"><ArrowLeft size={16} /> Dashboard</button>
        <div className="flex items-center gap-3 mb-6">
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[#E07A5F] text-white"><ClipboardList size={20} /></span>
          <div>
            <h1 className="font-head text-4xl font-bold text-[#3D405B]">Manage Assignments</h1>
            <p className="text-[#6B7280] text-sm">Tap the checkboxes to select assignments, then <b>assign them to other students</b>, reschedule, or delete — all at once.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input data-testid="ma-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, subject…"
              className="w-full rounded-full border border-[#EAE6DF] bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]" />
          </div>
          <select data-testid="ma-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
            className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
            <option value="">All students</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
          </select>
          <button data-testid="push-forward-btn" onClick={openPush}
            className="flex items-center gap-1.5 rounded-full bg-[#F4A261] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#e0924f] transition-colors">
            <FastForward size={15} /> Push forward
          </button>
          <button data-testid="catch-up-btn" onClick={() => setCatchOpen(true)}
            className="flex items-center gap-1.5 rounded-full bg-[#81B29A] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#6f9d87] transition-colors">
            <Zap size={15} /> Catch up overdue
          </button>
        </div>

        {loading ? <p className="text-[#6B7280]">Loading…</p> : visible.length === 0 ? (
          <p className="text-[#6B7280]" data-testid="ma-empty">No assignments found.</p>
        ) : (
          <div className="space-y-8">
            {Object.entries(groups).map(([sid, list]) => {
              const groupIds = list.map((a) => a.id);
              const allSel = groupIds.every((id) => selected.has(id));
              const toggleGroup = () => setSelected((s) => { const n = new Set(s); allSel ? groupIds.forEach((id) => n.delete(id)) : groupIds.forEach((id) => n.add(id)); return n; });
              return (
              <div key={sid}>
                <h2 className="font-head text-lg font-semibold text-[#3D405B] mb-3 flex items-center gap-3">
                  <button data-testid={`ma-select-all-${sid}`} onClick={toggleGroup} className="text-[#6B7280] hover:text-[#E07A5F]">{allSel ? <CheckSquare size={18} className="text-[#E07A5F]" /> : <Square size={18} />}</button>
                  {nameOf(sid)} <span className="text-sm font-normal text-[#6B7280]">· {list.length} assignment{list.length === 1 ? "" : "s"}</span>
                </h2>
                <div className="space-y-2">
                  {list.sort((x, y) => (x.due_date || "").localeCompare(y.due_date || "")).map((a, i) => {
                    const color = SUBJECT_COLOR[a.subject] || "#81B29A";
                    return (
                      <motion.div key={a.id} data-testid={`ma-row-${a.id}`}
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.3) }}
                        className="flex items-center justify-between gap-4 rounded-2xl bg-white border border-[#EAE6DF] px-5 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <button data-testid={`ma-select-${a.id}`} onClick={() => toggle(a.id)} className="shrink-0 text-[#6B7280] hover:text-[#E07A5F]">
                            {selected.has(a.id) ? <CheckSquare size={20} className="text-[#E07A5F]" /> : <Square size={20} />}
                          </button>
                          <span className="w-1.5 h-10 rounded-full shrink-0" style={{ background: color }} />
                          <div className="min-w-0">
                            <p className="font-head font-semibold text-[#3D405B] truncate">{a.title}</p>
                            <p className="text-xs text-[#6B7280] truncate">
                              <span className="font-semibold uppercase tracking-wide" style={{ color }}>{a.subject}</span>
                              {a.chapter && <span> · {a.chapter}</span>}
                              {a.due_date && <span> · Due {new Date(a.due_date.slice(0, 10) + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
                              {!a.published && <span className="ml-1 text-[#F4A261]">· Draft</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button data-testid={`ma-library-${a.id}`} onClick={() => saveToLibrary(a)} disabled={savingLibId === a.id} title="Save to Lesson Library"
                            className="flex items-center gap-1.5 rounded-full border border-[#EAE6DF] px-4 py-2 text-sm text-[#6B7280] hover:border-[#457B9D] hover:text-[#457B9D] transition-colors disabled:opacity-60">
                            <Library size={15} /> {savingLibId === a.id ? "Saving…" : "Library"}
                          </button>
                          <button data-testid={`ma-edit-${a.id}`} onClick={() => navigate(`/parent/edit-assignment/${a.id}`)} title="Edit assignment"
                            className="flex items-center gap-1.5 rounded-full border border-[#EAE6DF] px-4 py-2 text-sm text-[#6B7280] hover:border-[#81B29A] hover:text-[#81B29A] transition-colors">
                            <Pencil size={15} /> Edit
                          </button>
                          <button data-testid={`ma-delete-${a.id}`} onClick={() => setToDelete(a)} title="Delete assignment"
                            className="flex items-center gap-1.5 rounded-full border border-[#EAE6DF] px-4 py-2 text-sm text-[#6B7280] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors">
                            <Trash2 size={15} /> Delete
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );})}
          </div>
        )}
      </main>

      {toDelete && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !deleting && setToDelete(null)}>
          <div data-testid="ma-delete-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#E07A5F]/15 text-[#E07A5F] mb-4"><AlertTriangle size={22} /></span>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">Delete this assignment?</h2>
            <p className="text-[#6B7280] mt-2 leading-relaxed">This permanently removes <b>{toDelete.title}</b> for <b>{nameOf(toDelete.student_id)}</b>, takes it off the calendar/schedule, and deletes any saved grade or progress for it. This can't be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="ma-delete-cancel" onClick={() => setToDelete(null)} disabled={deleting}
                className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">Cancel</button>
              <button data-testid="ma-delete-confirm" onClick={doDelete} disabled={deleting}
                className="flex-1 rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{deleting ? "Deleting…" : "Delete assignment"}</button>
            </div>
          </div>
        </div>
      )}
      {selected.size > 0 && (
        <div data-testid="bulk-bar" className="fixed bottom-0 inset-x-0 z-[55] border-t border-[#EAE6DF] bg-white/95 backdrop-blur">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-[#3D405B]">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button data-testid="bulk-clear" onClick={clearSel} className="rounded-full border border-[#EAE6DF] px-4 py-2 text-sm text-[#6B7280] hover:border-[#3D405B] transition-colors">Clear</button>
              <button data-testid="bulk-reschedule" onClick={() => setReschedOpen(true)} className="flex items-center gap-1.5 rounded-full border border-[#F4A261] text-[#c67c3f] px-4 py-2 text-sm font-semibold hover:bg-[#F4A261]/10 transition-colors"><CalendarClock size={15} /> Reschedule</button>
              <button data-testid="bulk-library" onClick={doBulkSaveLibrary} disabled={working} className="flex items-center gap-1.5 rounded-full border border-[#457B9D] text-[#457B9D] px-4 py-2 text-sm font-semibold hover:bg-[#457B9D]/10 transition-colors disabled:opacity-60"><Library size={15} /> Save to Library</button>
              <button data-testid="bulk-copy" onClick={() => setCopyOpen(true)} className="flex items-center gap-1.5 rounded-full bg-[#81B29A] text-white px-4 py-2 text-sm font-semibold hover:bg-[#6f9d87] transition-colors"><Users size={15} /> Assign to students</button>
              <button data-testid="bulk-delete" onClick={() => setBulkDelete(true)} className="flex items-center gap-1.5 rounded-full bg-[#E07A5F] text-white px-4 py-2 text-sm font-semibold hover:bg-[#c96851] transition-colors"><Trash2 size={15} /> Delete selected</button>
            </div>
          </div>
        </div>
      )}

      {bulkDelete && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !working && setBulkDelete(false)}>
          <div data-testid="bulk-delete-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-[#E07A5F]/15 text-[#E07A5F] mb-4"><AlertTriangle size={22} /></span>
            <h2 className="font-head text-2xl font-bold text-[#3D405B]">Delete {selected.size} assignment{selected.size === 1 ? "" : "s"}?</h2>
            <p className="text-[#6B7280] mt-2 leading-relaxed">This permanently removes the selected assignments, takes them off the calendar, and deletes any saved grades or progress. This can't be undone.</p>
            <div className="flex gap-3 mt-6">
              <button data-testid="bulk-delete-cancel" onClick={() => setBulkDelete(false)} disabled={working} className="flex-1 rounded-full border border-[#EAE6DF] py-2.5 font-semibold text-[#3D405B] hover:border-[#3D405B] transition-colors disabled:opacity-60">Cancel</button>
              <button data-testid="bulk-delete-confirm" onClick={doBulkDelete} disabled={working} className="flex-1 rounded-full bg-[#E07A5F] text-white py-2.5 font-semibold hover:bg-[#c96851] transition-colors disabled:opacity-60">{working ? "Deleting…" : "Delete them"}</button>
            </div>
          </div>
        </div>
      )}

      {reschedOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !working && setReschedOpen(false)}>
          <div data-testid="reschedule-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-head text-2xl font-bold text-[#3D405B]"><CalendarClock size={20} className="text-[#F4A261]" /> Reschedule</span>
              <button onClick={() => setReschedOpen(false)} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
            </div>
            <p className="text-[#6B7280] text-sm mb-4">Change the due date of the <b>{selected.size}</b> selected assignment{selected.size === 1 ? "" : "s"}.</p>
            <div className="flex gap-2 mb-5 rounded-full bg-[#F7F3EE] p-1">
              <button data-testid="resched-mode-set" onClick={() => setReschedMode("set")}
                className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${reschedMode === "set" ? "bg-white text-[#3D405B] shadow-sm" : "text-[#6B7280]"}`}>Set to a date</button>
              <button data-testid="resched-mode-shift" onClick={() => setReschedMode("shift")}
                className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${reschedMode === "shift" ? "bg-white text-[#3D405B] shadow-sm" : "text-[#6B7280]"}`}>Shift by days</button>
            </div>
            {reschedMode === "set" ? (
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#3D405B] mb-1.5">New due date</label>
                <input data-testid="resched-date" type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)}
                  className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 text-[#3D405B] focus:border-[#F4A261] focus:outline-none" />
                <p className="text-xs text-[#6B7280] mt-2">Every selected assignment will be moved to this date.</p>
              </div>
            ) : (
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#3D405B] mb-1.5">Shift by (days)</label>
                <input data-testid="resched-shift" type="number" value={reschedShift}
                  onChange={(e) => setReschedShift(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
                  className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 text-[#3D405B] focus:border-[#F4A261] focus:outline-none" />
                <p className="text-xs text-[#6B7280] mt-2">Use a negative number to move earlier (e.g. <b>-3</b>). Assignments without a due date are skipped.</p>
              </div>
            )}
            <button data-testid="resched-confirm" onClick={doReschedule} disabled={working} className="w-full flex items-center justify-center gap-2 rounded-full bg-[#F4A261] text-white py-3 font-semibold hover:bg-[#e0924f] transition-colors disabled:opacity-60"><CalendarClock size={18} /> {working ? "Rescheduling…" : "Reschedule"}</button>
          </div>
        </div>
      )}

      {copyOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !working && setCopyOpen(false)}>
          <div data-testid="copy-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-head text-2xl font-bold text-[#3D405B]"><Users size={20} className="text-[#81B29A]" /> Assign to students</span>
              <button onClick={() => setCopyOpen(false)} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
            </div>
            <p className="text-[#6B7280] text-sm mb-4">Give the <b>{selected.size}</b> selected assignment{selected.size === 1 ? "" : "s"} to one or more students. Each student gets their own fresh copy (no grades). A student who already has an assignment is skipped.</p>
            <div className="space-y-2 max-h-64 overflow-y-auto mb-5">
              {students.map((s) => {
                const on = copyTargets.has(s.id);
                return (
                  <button key={s.id} data-testid={`copy-target-${s.id}`} onClick={() => setCopyTargets((t) => { const n = new Set(t); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; })}
                    className={`w-full flex items-center gap-3 rounded-xl border px-4 py-2.5 text-sm transition-colors ${on ? "border-[#81B29A] bg-[#81B29A]/5" : "border-[#EAE6DF] hover:border-[#81B29A]/40"}`}>
                    {on ? <CheckSquare size={18} className="text-[#81B29A]" /> : <Square size={18} className="text-[#6B7280]" />}
                    <span className="text-[#3D405B]">{s.first_name} {s.last_name}</span>
                  </button>
                );
              })}
            </div>
            <button data-testid="copy-confirm" onClick={doCopy} disabled={working} className="w-full flex items-center justify-center gap-2 rounded-full bg-[#81B29A] text-white py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60"><Users size={18} /> {working ? "Assigning…" : "Assign to selected students"}</button>
          </div>
        </div>
      )}
      {pushOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !working && setPushOpen(false)}>
          <div data-testid="push-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-head text-2xl font-bold text-[#3D405B]"><FastForward size={20} className="text-[#F4A261]" /> Push forward</span>
              <button onClick={() => setPushOpen(false)} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
            </div>
            <p className="text-[#6B7280] text-sm mb-4">Move your schedule later to catch up on overdue work.</p>

            <label className="block text-sm font-medium text-[#3D405B] mb-1.5">Push forward by (days)</label>
            <div className="flex items-center gap-2 mb-4">
              <input data-testid="push-days" type="number" value={pushDays}
                onChange={(e) => setPushDays(e.target.value === "" ? "" : parseInt(e.target.value, 10))}
                className="w-28 rounded-xl border border-[#EAE6DF] px-4 py-2.5 text-[#3D405B] focus:border-[#F4A261] focus:outline-none" />
              <div className="flex gap-1.5">
                {[7, 14, 30].map((d) => (
                  <button key={d} data-testid={`push-preset-${d}`} onClick={() => setPushDays(d)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${pushDays === d ? "border-[#F4A261] bg-[#F4A261]/10 text-[#c67c3f]" : "border-[#EAE6DF] text-[#6B7280] hover:border-[#F4A261]"}`}>
                    {d === 7 ? "1 week" : d === 14 ? "2 weeks" : "1 month"}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2 mb-5">
              <button data-testid="push-scope-all" onClick={() => setPushScope("all")}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${pushScope === "all" ? "border-[#F4A261] bg-[#F4A261]/5" : "border-[#EAE6DF] hover:border-[#F4A261]/50"}`}>
                <p className="font-semibold text-[#3D405B] text-sm">Push all unfinished lessons</p>
                <p className="text-xs text-[#6B7280] mt-0.5">Every not‑yet‑done lesson {filter ? `for ${nameOf(filter)}` : "for all three kids"} moves later, keeping its spacing. Already‑completed lessons stay put.</p>
              </button>
              <button data-testid="push-scope-selected" onClick={() => setPushScope("selected")}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${pushScope === "selected" ? "border-[#F4A261] bg-[#F4A261]/5" : "border-[#EAE6DF] hover:border-[#F4A261]/50"}`}>
                <p className="font-semibold text-[#3D405B] text-sm">Push only selected ({selected.size})</p>
                <p className="text-xs text-[#6B7280] mt-0.5">Move just the lessons you've checked.</p>
              </button>
            </div>

            {pushScope === "all" && (
              <div className="mb-5">
                <label className="block text-sm font-medium text-[#3D405B] mb-1.5">Only move lessons on/after (optional)</label>
                <input data-testid="push-from-date" type="date" value={pushFrom} onChange={(e) => setPushFrom(e.target.value)}
                  className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 text-[#3D405B] focus:border-[#F4A261] focus:outline-none" />
                <p className="text-xs text-[#6B7280] mt-1">Leave blank to move all unfinished lessons. Set a date to only shift lessons from that day onward.</p>
              </div>
            )}

            <button data-testid="push-confirm" onClick={doPushForward} disabled={working}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#F4A261] text-white py-3 font-semibold hover:bg-[#e0924f] transition-colors disabled:opacity-60">
              <FastForward size={18} /> {working ? "Pushing…" : "Push forward"}
            </button>
          </div>
        </div>
      )}
      {catchOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-6 bg-[#3D405B]/60 backdrop-blur-sm" onClick={() => !working && setCatchOpen(false)}>
          <div data-testid="catch-up-modal" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-8">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 font-head text-2xl font-bold text-[#3D405B]"><Zap size={20} className="text-[#81B29A]" /> Catch up overdue</span>
              <button onClick={() => setCatchOpen(false)} className="text-[#6B7280] hover:text-[#3D405B]"><X size={20} /></button>
            </div>
            <p className="text-[#6B7280] text-sm mb-5">
              This moves every <b>overdue, not‑yet‑done</b> lesson {filter ? `for ${nameOf(filter)}` : "for all three kids"} forward so it resumes the <b>coming Monday</b> — keeping each child's order and spacing, and never landing on a Friday. Already‑completed lessons stay put.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setCatchOpen(false)} className="flex-1 rounded-full border border-[#EAE6DF] py-3 hover:border-[#3D405B] transition-colors">Cancel</button>
              <button data-testid="catch-up-confirm" onClick={doCatchUp} disabled={working}
                className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[#81B29A] text-white py-3 font-semibold hover:bg-[#6f9d87] transition-colors disabled:opacity-60">
                <Zap size={18} /> {working ? "Catching up…" : "Catch up now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
